import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './SuccessPage.module.css';
import OrderStatus from './OrderStatus';

interface SuccessPageProps {
  onCartRefresh: () => void;
}

const SuccessPage: React.FC<SuccessPageProps> = ({ onCartRefresh }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orderData, setOrderData] = useState<any>(null);

  useEffect(() => {
    const fetchOrderData = async () => {
      // Get session ID from URL params
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        console.error('No session ID found in URL');
        return;
      }

      try {
        console.log('Fetching order data for session:', sessionId);
        
        // Fetch order data from API
        const response = await fetch(`http://localhost:5000/api/order/${sessionId}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch order: ${response.status}`);
        }
        
        const orderData = await response.json();
        console.log('Order data received from API:', orderData);
        
        // Set the order data from API
        setOrderData(orderData);
        
        // Clear the cart from localStorage after successful purchase
        window.localStorage.setItem('cartItems', '[]');
        
        // Refresh the cart component and navbar number
        setTimeout(() => {
          onCartRefresh();
        }, 100);
        
        // Store order information for future reference
        const orderInfo = {
          orderNumber: orderData.id,
          email: orderData.customerEmail,
          orderDate: orderData.createdAt,
          items: orderData.items,
          total: orderData.totalAmount,
          status: orderData.status,
          shippingFee: orderData.shippingFee,
          taxAmount: orderData.taxAmount,
          shippingMethod: orderData.shippingMethod,
          shippingAddress: orderData.shippingAddress
        };
        
        // Store in localStorage for persistence
        window.localStorage.setItem(
          `order_${orderData.id}`,
          JSON.stringify(orderInfo)
        );
        
        // Store in orders list
        const existingOrders = window.localStorage.getItem('orders') || '[]';
        try {
          const orders = JSON.parse(existingOrders);
          orders.push(orderInfo);
          window.localStorage.setItem('orders', JSON.stringify(orders));
        } catch (error) {
          console.error('Error storing orders:', error);
        }
        
        console.log('Order stored successfully:', orderInfo);
        
        // Clear the checkout email from localStorage
        window.localStorage.removeItem('checkoutEmail');
        
      } catch (error) {
        console.error('Error fetching order data:', error);
        
        // Fallback to localStorage data if API fails
        const purchasedItems = window.localStorage.getItem('cartItems');
        const customerEmail = window.localStorage.getItem('checkoutEmail');
        
        let orderItems = [];
        if (purchasedItems) {
          try {
            orderItems = JSON.parse(purchasedItems);
          } catch (error) {
            console.error('Error parsing cart items:', error);
          }
        }
        
        const fallbackOrderData = {
          id: 'ERROR',
          status: 'paid',
          shippingStatus: 'pending',
          customerEmail: customerEmail || 'test@example.com',
          items: orderItems,
          totalAmount: orderItems.reduce(
            (sum: number, item: any) => sum + item.price * item.quantity,
            0
          ),
          shippingFee: 0,
          taxAmount: 0
        };
        
        setOrderData(fallbackOrderData);
      }
    };

    fetchOrderData();
  }, [searchParams]);

  return (
    <div className={styles.successPage}>
      <div className={styles.successContainer}>
        {/* Success Icon and Title */}
        <div className={styles.successCard}>
          <div className={styles.successHeader}>
            <div className={styles.successIcon}>
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className={styles.successTitle}>Payment Successful!</h1>
          </div>
          <p className={styles.successMessage}>
            Thank you for your purchase! Your payment has been processed
            successfully.
          </p>

          {/* Spam Disclaimer */}
          <div className={styles.spamDisclaimer}>
            <p className={styles.spamDisclaimerText}>
              📧 <strong>Important:</strong> Please check your spam/junk folder
              if you don't receive your order confirmation email within a few
              minutes.
            </p>
          </div>

          {/* Order Details */}
          {orderData && (
            <div className={styles.orderDetails}>
              <h3 className={styles.orderDetailsTitle}>Order Details:</h3>
              <p className={styles.orderDetailItem}>
                <span className={styles.orderDetailLabel}>Order Number:</span>{' '}
                {orderData.id}
              </p>
              {orderData.customerEmail && (
                <p className={styles.orderDetailItem}>
                  <span className={styles.orderDetailLabel}>Email:</span>{' '}
                  {orderData.customerEmail}
                </p>
              )}
              <p className={styles.orderDetailItem}>
                <span className={styles.orderDetailLabel}>Order Date:</span>{' '}
                {new Date().toLocaleDateString()}
              </p>
              <p className={styles.orderDetailItem}>
                <span className={styles.orderDetailLabel}>Confirmation:</span>{' '}
                Sent to your email
              </p>
              <p className={styles.orderDetailItem}>
                <span className={styles.orderDetailLabel}>Total Amount:</span> $
                {orderData.totalAmount ? orderData.totalAmount.toFixed(2) : '0.00'}
              </p>
            </div>
          )}

          <button
            onClick={() => navigate('/')}
            className={styles.continueShoppingButton}
          >
            Continue Shopping
          </button>
        </div>

        {/* Order Status */}
        {orderData && (
          <div className={styles.orderStatusSection}>
            <OrderStatus
              orderStatus={orderData.status}
              shippingStatus={orderData.shippingStatus || 'pending'}
              orderItems={orderData.items || orderData.orderItems}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SuccessPage;
